<template>
  <div></div>
</template>

<script>
import Vue from "vue";
import * as turf from "@turf/turf";
import { mapGetters } from "vuex";
import { MapLayer, MapSource } from "@/config/Map.js";
import { createNamespacedHelpers } from "vuex";
import mapboxgl from "mapbox-gl";
import AisService from "@/services/aisService";
import { DateUtil } from "@/utils";
import { VesselMixin } from "@/mixins";
import LocalDbService from "@/services/localDbService";
import moment from "moment-timezone";

// Store
const {
  mapActions: mapActionsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
} = createNamespacedHelpers("liveMap");

export default {
  name: "VesselMapLayer",
  mixins: [VesselMixin],
  data() {
    return {
      initialized: false,
      popupForVessel: new mapboxgl.Popup({
        offset: 15,
        className: "live-map-popup-for-vessel",
        closeButton: false,
        closeOnClick: false,
      }),
      popupForWake: new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      }),
      circleIconUrl: require("@/assets/images/vessel/image_circle.png"),
      isPopup: false,
      clicked: false,
    };
  },
  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
  },
  computed: {
    ...mapGettersOfLiveMap([
      "hasWakes",
      "isDisplayedVesselDetail",
      "isMonitoringLimit",
      "isMonitoring",
      "getWakeLineInfo",
    ]),
    ...mapStateOfLiveMap({
      filterVessels: state => state.filterVessels,
    }),
    ...mapGetters({
      enterpriseVessels: "enterpriseVessel/getVessels",
      vesselPositions: "enterpriseMapVessel/getVesselPositions",
      selectedVesselSize: "enterpriseMapVesselSelected/getVesselsSize",
      filteredVesselCategories: "enterpriseMapSetting/getVesselCategories",
    }),
    isFreeAccount() {
      return this.$store.state.isFree === true;
    },
  },
  watch: {
    mapEvent() {
      this.removePopupForVessel(this);
    },
    isReady() {
      if (this.isReady) {
        this.isMapReady();
        this.setSubscribes();
        this.setFilters();
      }
      if (!this.initialized) {
        this.initialized = true;
      }
    },
    filteredVesselCategories() {
      this.filterByVesselCategories();
    },
  },
  methods: {
    ...mapActionsOfLiveMap([
      "showVesselDetail",
      "jumpTo",
      "showVesselDetail",
      "cleanUpVesselDetail",
      "getLastPosition",
      "addMonitoringVessel",
      "fetchWake",
      "removeWake",
      "setFilterVessels",
    ]),

    showPopup(e, self) {
      if (this.isPopup) return;
      this.isPopup = true;
      let coordinates = e.features[0].geometry.coordinates;
      // Polygonの場合
      if (e.features[0].geometry.type != "Point") {
        let collections = coordinates[0].map(c => {
          return turf.point(c);
        });
        let features = turf.featureCollection(collections);
        let center = turf.center(features);

        coordinates = center.geometry.coordinates;
      }

      let vesselId = e.features[0].properties.id;
      let job = [];
      job.push(AisService.getStaticInfo(vesselId));
      job.push(AisService.getDynamicAisInfo(vesselId));

      Promise.all(job).then(values => {
        let aisInfo = {
          s: values[0],
          d: values[1],
        };
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        let description = self.crateVesselInfoHtml(aisInfo);
        this.popupForVessel
          .setLngLat(coordinates)
          .setHTML(description)
          .addTo(self.map);
      });
    },

    removePopupForVessel(self) {
      self.popupForVessel.remove();
    },
    crateVesselInfoHtml(aisInfo) {
      let speedLabel = this.$t("Views.Vessel.Velocity");
      let speed = aisInfo.d.v !== undefined ? aisInfo.d.v : "0.0";
      let headingLabel = this.$t("Views.Vessel.Heading");
      let heading =
        aisInfo.d.h !== undefined || aisInfo.d.h === 0
          ? aisInfo.d.h
          : aisInfo.d.d !== undefined
          ? aisInfo.d.d
          : "0";
      let lastReceivedLabel = this.$t("Views.Vessel.LastReceived");
      let lastReceived =
        aisInfo.d.t !== undefined
          ? DateUtil.elapsedTimeByTimestamp(aisInfo.d.t)
          : "---";
      let categoryLabel = this.$t("Views.Vessel.Category");
      let category =
        aisInfo.s.c !== undefined
          ? this.vesselCategory.properties[aisInfo.s.c].name_ja
          : "---";
      let html = `<span class="mr-2">${speedLabel}: <strong>${speed} kn</strong></span><span>${headingLabel}: <strong>${heading}°</strong></span><br><span class="mr-2">${categoryLabel}: <strong>${category}</strong></span><span>${lastReceivedLabel}: <strong>${lastReceived}</strong></span>`;
      return html;
    },
    setCursor(isPointer, self) {
      self.map.getCanvas().style.cursor = isPointer ? "pointer" : "";
    },
    showWakeLineInfoPopup(e, self) {
      let properties = e.features[0].properties;
      let coordinates = [e.lngLat.lng, e.lngLat.lat];
      let speedLabel = this.$t("Views.Vessel.Velocity");
      let speed = properties.speed;
      let dataTimeLabel = this.$t("Views.Commons.DataTime");
      let dataTime = properties.localDate;
      let vesselName = properties.vesselName;
      let wakeLineLabel = this.$t("Views.Vessel.WakeLine");
      let description = `<strong>${vesselName}</strong><span>の${wakeLineLabel}</span><br><span class="mr-2">${speedLabel}: <strong>${speed} kn</strong></span><span>${dataTimeLabel}: <strong>${dataTime}</strong></span>`;
      self.popupForWake
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(self.map);
    },
    removePopupForWake(self) {
      self.popupForWake.remove();
    },
    setPointerStyle() {
      let self = this;
      this.map.on("mousemove", MapLayer.VesselSymbolLayer.id, e => {
        self.setCursor(true, self);
        self.showPopup(e, self);
      });
      this.map.on("mouseleave", MapLayer.VesselSymbolLayer.id, () => {
        self.setCursor(false, self);
        this.isPopup = false;
        //self.removePopupForVessel(self);
      });
      this.map.on("mousemove", MapLayer.VesselCircleLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.VesselCircleLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", MapLayer.VesselPolygonLayer.id, e => {
        self.setCursor(true, self);
        self.showPopup(e, self);
      });
      this.map.on("mouseleave", MapLayer.VesselPolygonLayer.id, () => {
        self.setCursor(false, self);
        //self.removePopupForVessel(self);
        this.isPopup = false;
      });
      this.map.on("mousemove", MapLayer.VesselSymbolFarLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.VesselSymbolFarLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", MapLayer.VesselSizeWithoutLayer.id, e => {
        self.setCursor(true, self);
        self.showPopup(e, self);
      });
      this.map.on("mouseleave", MapLayer.VesselSizeWithoutLayer.id, () => {
        self.setCursor(false, self);
        //self.removePopupForVessel(self);
        this.isPopup = false;
      });
      this.map.on("mousemove", MapLayer.OwnVesselMarkerLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.OwnVesselMarkerLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", MapLayer.SelectedVesselMarkerLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.SelectedVesselMarkerLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on(
        "mousemove",
        MapLayer.VesselWakeLineLayerForClickEvent.id,
        e => {
          self.setCursor(true, self);
          self.showWakeLineInfoPopup(e, self);
        }
      );
      this.map.on(
        "mouseleave",
        MapLayer.VesselWakeLineLayerForClickEvent.id,
        () => {
          self.setCursor(false, self);
          self.removePopupForWake(self);
        }
      );

      this.map.on("mousemove", function() {
        if (!self.isPopup) {
          self.removePopupForVessel(self);
        }
      });
    },
    bindLayerClick() {
      // 船舶レイヤーのクリックイベント
      let onClickVessel = evt => {
        let vesselId = evt.features[0].properties.id;
        this.clicked = true;
        const onClickEvent = vesselId => {
          if (this.clicked) {
            this.showVesselDetail(vesselId);
            this.clicked = false;
          }
        };
        setTimeout(onClickEvent.bind(null, vesselId), 600);
      };

      this.map.on("click", MapLayer.VesselSymbolLayer.id, onClickVessel);
      this.map.on("click", MapLayer.VesselSymbolFarLayer.id, onClickVessel);
      this.map.on("click", MapLayer.VesselCircleLayer.id, onClickVessel);
      this.map.on("click", MapLayer.VesselPolygonLayer.id, onClickVessel);
      this.map.on("click", MapLayer.VesselSizeWithoutLayer.id, onClickVessel);

      // 自社船、選択船のマーカーレイヤーのクリックイベント
      let onClickMarker = async evt => {
        let vesselId = evt.features[0].properties.id;
        let lastPosition = await this.getLastPosition(vesselId);
        if (lastPosition) {
          let coordinates = {
            center: [lastPosition.longitude, lastPosition.latitude],
          };
          if (!this.hasWakes(vesselId)) {
            const payload = {
              started_at:
                moment()
                  .utc()
                  .subtract(1, "days")
                  .format("YYYY-MM-DDTHH:mm:ss.000") + "Z",
              ended_at:
                moment()
                  .utc()
                  .format("YYYY-MM-DDTHH:mm:ss.000") + "Z",
              vesselId: vesselId,
            };
            try {
              this.$vs.loading();
              await this.fetchWake(payload);
              this.$vs.loading.close();
              if (this.map.getZoom() <= 8) {
                this.jumpTo(coordinates);
              }
              await this.showVesselDetail(vesselId);
            } catch (error) {
              Vue.$log.error(error);
              this.$vs.loading.close();
              this.$vs.notify({
                title: "航跡がありません",
                text: "航跡がないため表示できません",
                color: "warning",
                position: "top-center",
              });
            }
          } else {
            await this.removeWake(vesselId);
            if (this.isDisplayedVesselDetail(vesselId)) {
              await this.cleanUpVesselDetail();
            }
          }
        }
      };
      this.map.on("click", MapLayer.OwnVesselMarkerLayer.id, onClickMarker);
      this.map.on(
        "click",
        MapLayer.SelectedVesselMarkerLayer.id,
        onClickMarker
      );

      // 船舶レイヤーのタブルクリックイベント
      let onDoubleClickVessel = async evt => {
        if (this.clicked) {
          this.clicked = false;
          if (this.isFreeAccount) {
            // フリーアカウントでは、航跡表示処理は行わない。
            return;
          }
          let vesselId = evt.features[0].properties.id;
          let lastPosition = await this.getLastPosition(vesselId);
          if (lastPosition) {
            let coordinates = {
              center: [lastPosition.longitude, lastPosition.latitude],
            };
            if (!this.hasWakes(vesselId)) {
              const payload = {
                started_at:
                  moment()
                    .utc()
                    .subtract(1, "days")
                    .format("YYYY-MM-DDTHH:mm:ss.000") + "Z",
                ended_at:
                  moment()
                    .utc()
                    .format("YYYY-MM-DDTHH:mm:ss.000") + "Z",
                vesselId: vesselId,
              };
              this.$vs.loading();
              try {
                await this.fetchWake(payload);
              } catch (error) {
                Vue.$log.error(error);
                this.$vs.notify({
                  title: "航跡がありません",
                  text: "航跡がないため表示できません",
                  color: "warning",
                  position: "top-center",
                });
              }
              if (this.map.getZoom() <= 8) {
                this.jumpTo(coordinates);
              }
              this.$vs.loading.close();
            } else {
              await this.removeWake(vesselId);
              if (this.isDisplayedVesselDetail(vesselId)) {
                await this.cleanUpVesselDetail();
              }
            }
          }
        }
      };

      this.map.on(
        "dblclick",
        MapLayer.VesselSymbolLayer.id,
        onDoubleClickVessel
      );
      this.map.on(
        "dblclick",
        MapLayer.VesselSymbolFarLayer.id,
        onDoubleClickVessel
      );
      this.map.on(
        "dblclick",
        MapLayer.VesselCircleLayer.id,
        onDoubleClickVessel
      );
      this.map.on(
        "dblclick",
        MapLayer.VesselPolygonLayer.id,
        onDoubleClickVessel
      );
      this.map.on(
        "dblclick",
        MapLayer.VesselSizeWithoutLayer.id,
        onDoubleClickVessel
      );
    },
    filterByVesselCategories(vesselCategoryFilters) {
      if (!this.map) return;

      this.map.setFilter(MapLayer.VesselSymbolLayer.id, [
        "all",
        vesselCategoryFilters,
      ]);
      this.map.setFilter(MapLayer.VesselSymbolFarLayer.id, [
        "all",
        vesselCategoryFilters,
        [">", "v", 0.2],
      ]);
      this.map.setFilter(MapLayer.VesselCircleLayer.id, [
        "all",
        vesselCategoryFilters,
        ["<=", "v", 0.2],
      ]);
      this.map.setFilter(MapLayer.VesselTextLayer.id, [
        "all",
        vesselCategoryFilters,
      ]);
      this.map.setFilter(MapLayer.VesselPolygonLayer.id, [
        "all",
        vesselCategoryFilters,
      ]);
      this.map.setFilter(MapLayer.VesselSizeWithoutLayer.id, [
        "all",
        vesselCategoryFilters,
        ["==", "is_size", 0],
      ]);
      this.map.setFilter(MapLayer.VesselVectorLayer.id, [
        "all",
        vesselCategoryFilters,
      ]);
      // this.map.setFilter(MapLayer.VesselWakeLineLayer.id, [
      //   "all",
      //   vesselCategoryFilters
      // ]);
    },
    isMapReady() {
      if (!this.initialized) {
        this.bindLayerClick();
        this.setPointerStyle();
      }

      // VesselVectorLineLayer
      this.map.addLayer({
        id: MapLayer.VesselVectorLayer.id,
        type: "line",
        source: MapSource.VesselVectorSource.id,
        minzoom: 10,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#DA514B",
          "line-width": 2.0,
          "line-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselWakeLineLayer
      this.map.addLayer({
        id: MapLayer.VesselWakeLineLayer.id,
        type: "line",
        source: MapSource.VesselWakeLineSource.id,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1],
        },
      });

      // VesselWakeLineLayerForClickEvent
      this.map.addLayer({
        id: MapLayer.VesselWakeLineLayerForClickEvent.id,
        type: "line",
        source: MapSource.VesselWakeLineSourceForClickEvent.id,
        paint: {
          "line-width": 20.0,
          "line-opacity": 0.0,
        },
      });

      // VesselSymbolLayer
      this.map.addLayer({
        id: MapLayer.VesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 10,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselCircleLayer
      this.map.loadImage(this.circleIconUrl, (error, image) => {
        if (error) throw error;
        this.map.addImage("sdf-circle", image, { sdf: true });
      });
      this.map.addLayer({
        id: MapLayer.VesselCircleLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 1,
        maxzoom: 10,
        filter: ["<=", "v", 0.2],
        layout: {
          "icon-allow-overlap": false,
          "icon-ignore-placement": false,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "sdf-circle",
          "icon-size": 0.6,
        },
        paint: {
          "icon-color": {
            type: "identity",
            property: "co",
          },
          "icon-halo-width": 0,
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselSymbolFarLayer
      this.map.addLayer({
        id: MapLayer.VesselSymbolFarLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 1,
        maxzoom: 10,
        filter: [">", "v", 0.2],
        layout: {
          "icon-allow-overlap": false,
          "icon-ignore-placement": false,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselPolygonLayer
      this.map.addLayer({
        id: MapLayer.VesselPolygonLayer.id,
        type: "fill",
        source: MapSource.VesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      // VesselSizeWithoutLayer
      this.map.addLayer({
        id: MapLayer.VesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 14,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselTextLayer
      this.map.addLayer({
        id: MapLayer.VesselTextLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 10,
        layout: {
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": {
            type: "identity",
            property: "tco",
          },
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });

      /*=========================================================================================
       選択船 レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: MapLayer.SelectedVesselVectorLayer.id,
        type: "line",
        source: MapSource.SelectedVesselVectorSource.id,
        minzoom: 8,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#DA514B",
          "line-width": 2.0,
          "line-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselPolygonLayer.id,
        type: "fill",
        source: MapSource.SelectedVesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        minzoom: 8,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        minzoom: 14,
        maxzoom: 24,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselTextLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        layout: {
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": "#EFAC48",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselMarkerLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselMarkerSource.id,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{ism}",
          "icon-offset": [0, -15],
        },
      });

      /*=========================================================================================
        自社船 レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: MapLayer.OwnVesselVectorLayer.id,
        type: "line",
        source: MapSource.OwnVesselVectorSource.id,
        minzoom: 8,
        maxzoom: 24,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#DA514B",
          "line-width": 2.0,
          "line-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselPolygonLayer.id,
        type: "fill",
        source: MapSource.OwnVesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        minzoom: 8,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        minzoom: 14,
        maxzoom: 24,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselTextLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        layout: {
          "icon-allow-overlap": true,
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": "#54B1FC",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselMarkerLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselMarkerSource.id,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{iom}",
          "icon-offset": [0, -15],
        },
      });
    },
    setSubscribes() {
      this.storeUnsubscribe = this.$store.subscribe((mutation, _) => {
        if (mutation.type === "liveMap/SET_FILTER_VESSELS") {
          let f = ["in", "c"].concat(
            this.filterVessels.map(item => parseInt(item, 10))
          );
          this.filterByVesselCategories(f);
        }
      });
    },
    async setFilters() {
      let val = await LocalDbService.getFilteredVessels();
      this.setFilterVessels(val);
    },
  },
};
</script>

<style lang="scss"></style>
